import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { isAiFeaturesEnabled } from "config/aiFeatureFlags";
import {
  readOrgAiFromServiceProvider,
  resolveTimesheetAiAccess,
  SP_SETTINGS_AI_UPDATED_EVENT,
} from "helpers/spAiFeatureAccess";

/** Dispatched after Pusher role_update → get-role-authenticate-user (auth.sagas.js). */
export const REFRESH_PERMISSIONS_EVENT = "refreshPermissions";

const emptyOrgState = () => ({
  loading: false,
  ai_enabled: false,
  orgTimesheetModule: false,
  orgCanAnalyse: false,
  orgCanBulkAnalyse: false,
  orgCanViewInsights: false,
});

const orgStateFromCache = () => {
  const cached = readOrgAiFromServiceProvider();
  if (!cached) {
    return emptyOrgState();
  }
  return { ...cached, loading: false };
};

/**
 * Organisation + role gates for timesheet AI UI (org layer wins; no admin bypass on org).
 * Re-reads role permissions when Pusher refreshes user.permissions (refreshPermissions).
 */
export const useTimesheetAiAccess = () => {
  const reduxPermissions = useSelector((state) =>
    state?.users?.getIn?.(["user", "permissions"])
  );

  const [orgState, setOrgState] = useState(() => {
    if (!isAiFeaturesEnabled) {
      return emptyOrgState();
    }
    return orgStateFromCache();
  });

  // Bumps when role permissions change so resolveTimesheetAiAccess re-reads localStorage.
  const [permissionsVersion, setPermissionsVersion] = useState(0);

  const loadOrgAccess = useCallback(() => {
    if (!isAiFeaturesEnabled) {
      setOrgState(emptyOrgState());
      return;
    }
    setOrgState(orgStateFromCache());
  }, []);

  const refreshRoleGates = useCallback(() => {
    setPermissionsVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    loadOrgAccess();
  }, [loadOrgAccess]);

  useEffect(() => {
    const onSpSettingsAiUpdated = () => {
      loadOrgAccess();
    };

    window.addEventListener(SP_SETTINGS_AI_UPDATED_EVENT, onSpSettingsAiUpdated);
    return () => {
      window.removeEventListener(
        SP_SETTINGS_AI_UPDATED_EVENT,
        onSpSettingsAiUpdated
      );
    };
  }, [loadOrgAccess]);

  useEffect(() => {
    const onPermissionsRefreshed = () => {
      refreshRoleGates();
    };

    window.addEventListener(REFRESH_PERMISSIONS_EVENT, onPermissionsRefreshed);
    return () => {
      window.removeEventListener(
        REFRESH_PERMISSIONS_EVENT,
        onPermissionsRefreshed
      );
    };
  }, [refreshRoleGates]);

  // Redux user.permissions updates in the same saga tick as refreshPermissions.
  useEffect(() => {
    if (reduxPermissions != null) {
      refreshRoleGates();
    }
  }, [reduxPermissions, refreshRoleGates]);

  return useMemo(
    () => resolveTimesheetAiAccess(orgState),
    [orgState, permissionsVersion]
  );
};

export default useTimesheetAiAccess;
